import { Note } from "@components/atoms/Note"
import { Button } from "@components/controls/Button"
import { LoadingResults } from "@components/layout/LoadingResults"
import { useCancelMyMembership, useLeaveBusinessTeam, useMyMembership, useReactivateSubscription } from "@hooks/queries"
import { useAlertOverlay } from "@hooks/useAlertOverlay"
import { MakeURL } from "@utils/create-url"
import { formatDateTime, formatNiceShortDate } from "@utils/dates"
import { tv } from "tailwind-variants"
import { SubscriptionStatusesEnum, UseMyMembershipQuery } from "../../../types.graphql"
import { MembershipAlertBanner } from "../components/MembershipAlertBanner"
import { twMerge } from "tailwind-merge"

const createStyles = tv({
  slots: {
    subheading: "type-title-l-medium border-b border-black pb-2",
    group: "stack-y-1 border-b border-black py-4",
    groupLabel: "type-title-s-medium",
    groupValue: "type-title-s-light",
    note: "type-ui-s-roman text-grey-mid",
  },
})

export function MembershipSummary({ data, simple }: { data: UseMyMembershipQuery | null | undefined; simple?: boolean }) {
  const status = data?.customer?.memberStatus
  const styles = createStyles()

  if (!data || !status) return null

  const expiryNotes = []

  if (status.expiryDate && status.expiresSoon && status.requiresAction && !status.nextBillDate) {
    expiryNotes.push(`Your membership will expire on ${formatNiceShortDate(status.expiryDate!)}.`)
  } else if (status.expiryDate && !status.expired) {
    expiryNotes.push(`Valid till ${formatNiceShortDate(status.expiryDate!)}. `)
  }
  if (status.nextBillDate) {
    expiryNotes.push(`Next payment is ${formatNiceShortDate(status.nextBillDate!)}. `)
  } else if (status.expired) {
    if (status.expiryDate!) {
      expiryNotes.push(`Membership expired ${formatNiceShortDate(status.expiryDate!)}.`)
    } else {
      expiryNotes.push(`Membership expired.`)
    }
  }

  return (
    <>
      <div className={styles.group()}>
        <p className={styles.groupLabel()}>Member number</p>
        <p className={twMerge(styles.groupValue(), status.expired ? "line-through" : "")}>{status.memberNumber}</p>
        {!status.isActive && !status.plan ? <Note danger>You do not have an active membership</Note> : null}
        {!!status.since && <div className={styles.note()}>Member since {formatNiceShortDate(status.since)}</div>}
        {!status.isActive && !status.plan ? (
          <div className="mt-2">
            <Button href="/join">Join</Button>
          </div>
        ) : null}
      </div>
      {/* Membership plan and subscription basics */}
      {status?.plan?.name && (
        <div className={styles.group()}>
          <p className={styles.groupLabel()}>
            {status.plan.slug === "design-business-member"
              ? "Design Business Member" + (data.customer?.businessTeam?.name ? ` (${data.customer?.businessTeam?.name})` : "")
              : status.plan.name.replace(/\s+Membership$/, "") + " Membership"}
          </p>
          <p className={styles.groupValue()}>{status.price}</p>
          <div className="stack-y-3">
            <Note>{expiryNotes.join("")}</Note>
            {status.subscription?.status === SubscriptionStatusesEnum.PendingCancel && (
              <Note danger>Your subscription is pending cancellation. Member benefits will be available until {formatDateTime(status.subscription!.endDate!)}.</Note>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export function MembershipScreen() {
  const membership = useMyMembership(undefined)

  const cancelMembership = useCancelMyMembership()
  const reactivateSubscription = useReactivateSubscription()
  const leaveTeam = useLeaveBusinessTeam()
  const cancelDialog = useAlertOverlay()

  if (membership.isLoading) {
    return <LoadingResults />
  }

  const status = membership.data?.customer?.memberStatus

  // The status can be missing if the auth token has expired or the request
  // otherwise failed — don't treat it as "no membership", offer a retry.
  if (!status) {
    return (
      <div className="grid-auto">
        <div className="col-span-page-md stack-y-4">
          <Note danger>We couldn't load your membership details. Please try again.</Note>
          <div>
            <Button
              onClick={() => {
                membership.refetch()
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="grid-auto">
      <div className="col-span-page-md">
        <MembershipAlertBanner data={membership.data} />
        <MembershipSummary data={membership.data} />

        {/* Actions */}
        <div className="mt-5 flex gap-2">
          {/* Renew/Update Payment Link */}
          {status.expired ? (
            <Button href={MakeURL.renew()}>Renew Membership</Button>
          ) : status.needsPayment ? (
            <Button href={MakeURL.account("billing")}>Update Payment</Button>
          ) : status.expiresSoon && status.requiresAction ? (
            <Button href={MakeURL.renew()}>Renew</Button>
          ) : null}

          {/* Cancel Subscription */}
          {status.subscription?.status === SubscriptionStatusesEnum.Active && status.subscription && (
            <Button
              variant="outline"
              onClick={() => {
                cancelDialog.open({
                  title: "Are you absolutely sure?",
                  message: `Your membership is valid until ${formatDateTime(status.subscription?.endDate!)}, so you'll still have access to member benefits until then.`,
                  canCancel: true,
                  cancelLabel: "No, keep my membership",
                  confirmLabel: "Yes, cancel my membership",
                  onConfirm: async () => {
                    await cancelMembership.mutateAsync({
                      id: status.subscription?.id!,
                    })
                    membership.refetch()
                  },
                })
              }}
            >
              Stop my membership
            </Button>
          )}

          {/* Reactivate after pending cancellation */}
          {status.subscription?.status === SubscriptionStatusesEnum.PendingCancel && status.subscription && (
            <Button
              variant="outline"
              onClick={() => {
                cancelDialog.open({
                  title: "Reactivate your membership?",
                  message:
                    "You had previously requested to cancel your membership, however it is still within the cancellation period. Would you like to reactivate your membership?",
                  canCancel: true,
                  cancelLabel: "Cancel",
                  confirmLabel: "Yes, reactivate my membership",
                  onConfirm: async () => {
                    await reactivateSubscription.mutateAsync({
                      id: status.subscription?.id!,
                    })
                    membership.refetch()
                  },
                })
              }}
            >
              Reactivate membership
            </Button>
          )}

          {status.plan?.slug === "design-business" && (
            <>
              <Button variant="outline" href="/account/team">
                Manage team
              </Button>
            </>
          )}

          {status.plan?.slug === "design-business-member" && (
            <Button
              variant="outline"
              onClick={() => {
                cancelDialog.open({
                  title: "Leave team?",
                  message: `Are you sure you want to leave this team?`,
                  canCancel: true,
                  confirmLabel: "Yes, remove me from the team",
                  onConfirm: async () => {
                    await leaveTeam.mutateAsync(undefined)
                    membership.refetch()
                  },
                })
              }}
            >
              Leave team
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
